import type { PanVerificationStatus } from "@entities/campaigns/lib/hooks/useVerifyPan";
import { useVerifyPan } from "@entities/campaigns/lib/hooks/useVerifyPan";
import { ToastService } from "@shared/lib/services";
import { Button } from "@shared/ui/Button";
import { Flex, Input } from "antd";
import type { ChangeEvent } from "react";
import styles from "../styles.module.scss";

export const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/i;

type PanVerifyFieldProps = {
  value?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  status: PanVerificationStatus | null;
  onStatusChange: (status: PanVerificationStatus | null) => void;
};

export function PanVerifyField({
  value,
  onChange,
  status,
  onStatusChange,
}: PanVerifyFieldProps) {
  const { verifyPan, isVerifyingPan } = useVerifyPan();

  const normalizedPan = (value || "").trim().toUpperCase();

  const handleVerify = async () => {
    if (!PAN_PATTERN.test(normalizedPan)) {
      ToastService.error("Enter a valid PAN (e.g. ABCDE1234F)");
      return;
    }

    onStatusChange(null);
    try {
      const result = await verifyPan(normalizedPan);
      onStatusChange(result.status);
      if (result.status === "Valid") {
        ToastService.success("PAN verified successfully");
      } else {
        ToastService.error("PAN is invalid");
      }
    } catch {
      onStatusChange(null);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onStatusChange(null);
    onChange?.(event);
  };

  return (
    <Flex vertical gap={8}>
      <Flex gap={8} align="stretch">
        <Input
          value={value}
          onChange={handleChange}
          placeholder="PAN Card Number"
          maxLength={10}
          className={styles.panInput}
          disabled={isVerifyingPan}
        />
        <Button
          type="link"
          onClick={handleVerify}
          loading={isVerifyingPan}
          disabled={!normalizedPan || isVerifyingPan}
        >
          Verify
        </Button>
      </Flex>
      {status && (
        <span
          className={`${styles.panStatus} ${
            status === "Valid" ? styles.panStatusValid : styles.panStatusInvalid
          }`}
        >
          {status === "Valid" ? "PAN is valid" : "PAN is invalid"}
        </span>
      )}
    </Flex>
  );
}
